import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { authSlice } from '../store/reducers/AuthSlice';
import { refreshTokenQuery } from './auth/refreshTokenQuery';

type ApiError = {
  ok: false;
  message: string;
  errorCode?: string;
};

const mutex = new Mutex();

const baseUrl = process.env.REACT_APP_API_URL;

function setBaseUrl() {
  return baseUrl;
}

const baseQueryWithAccessToken = fetchBaseQuery({
  baseUrl: setBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as any;

    const accessToken =
      state.authReducer.user && state.authReducer.user.accessToken;

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }

    return headers;
  },
  credentials: 'include',
});

const baseQuery = baseQueryWithAccessToken;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const { setCredentials } = authSlice.actions;
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  const errorData = result.error?.data as ApiError | undefined;

  if (errorData?.errorCode === 'ONBOARDING_REQUIRED') {
    api.dispatch({ type: 'RESET_APP_STATE' });
  }

  if (result.error && result.error.status === 401) {
    // try to get a new token

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const refreshResult: any = await baseQuery(
          refreshTokenQuery(),
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          api.dispatch(setCredentials(refreshResult.data));
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          console.log('Failed to refresh token:', refreshResult.error);
          api.dispatch({ type: 'RESET_APP_STATE' });
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export default baseQueryWithReauth;
